import { CalciteOption } from "@esri/calcite-components-react";
import { renderAutocompleteField } from "components/AutocompleteInput";
import { HouseNumberInput } from "components/HouseNumberInput";
import { Input } from "components/Input";
import NenaIDSelect, { MigrateForm } from "components/NenaIDSelect";
import { NewCPNInput } from "components/NewCPNInput";
import { Select } from "components/Select";
import { useState } from "react";
import {
  ALIRecordRequest,
  classOfServiceOptions,
  functionOfChangeCreateOptions,
  functionOfChangeEditOptions,
  functionOfChangeMigrateOptions,
  functionOfChangeOptions,
  typeOfServiceOptions,
} from "services/ALIRecord.d";
import { ALIViewProps } from "../ALIForm";
import { ADDLDataSection } from "./ADDLEditViews";
import { AdditionalInfoSection } from "./AdditionalInformationSection";

// Component views
export function ALIWriteViews({
  recordRequest,
  setRecordRequest,
  record,
  handleChange,
  hasAddlDataPermission,
  addlDataRequest,
  setAddlDataRequest,
  lastFired,
  lastMSAGRecord,
  setLastMSAGRecord,
  availableOptions,
  setAvailableOptions,
  validationErrors,
  setValidationErrors,
  mode,
}: ALIViewProps) {
  const [showAdditionalInfo, setShowAdditionalInfo] = useState<boolean>(false);

  return (
    <div style={{ padding: "20px", paddingBottom: "40px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "100px",
        }}
      >
        {/* Left Column - Address Information */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <NewCPNInput
            recordRequest={recordRequest}
            setRecordRequest={setRecordRequest}
            mode={mode}
            fillOutAdditionalFieldsOnFinish={mode === "create" ? true : false}
          />
          <Input
            name="NAM"
            label="Customer Name*"
            value={recordRequest.NAM ?? record.NAM ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={32}
          />
          {renderAutocompleteField(
            "STA",
            "State*",
            "State",
            recordRequest,
            setRecordRequest,
            lastMSAGRecord,
            setLastMSAGRecord,
            availableOptions,
            setAvailableOptions,
            lastFired,
            validationErrors,
            setValidationErrors,
            false,
            mode === "edit" ? true : false,
            mode === "create" || mode === "migrate" ? true : false,
          )}
          {renderAutocompleteField(
            "MCN",
            "MSAG Community Name*",
            "MCN",
            recordRequest,
            setRecordRequest,
            lastMSAGRecord,
            setLastMSAGRecord,
            availableOptions,
            setAvailableOptions,
            lastFired,
            validationErrors,
            setValidationErrors,
            false,
            mode === "edit" ? true : false,
            mode === "create" || mode === "migrate" ? true : false,
          )}
          {renderAutocompleteField(
            "STN",
            "Street Name*",
            "StreetName",
            recordRequest,
            setRecordRequest,
            lastMSAGRecord,
            setLastMSAGRecord,
            availableOptions,
            setAvailableOptions,
            lastFired,
            validationErrors,
            setValidationErrors,
            false,
            mode === "edit" ? true : false,
            mode === "create" || mode === "migrate" ? true : false,
          )}
          {renderAutocompleteField(
            "STS",
            "Street Suffix*",
            "StreetSuffix",
            recordRequest,
            setRecordRequest,
            lastMSAGRecord,
            setLastMSAGRecord,
            availableOptions,
            setAvailableOptions,
            lastFired,
            validationErrors,
            setValidationErrors,
            false,
            mode === "edit" ? true : false,
            mode === "create" || mode === "migrate" ? true : false,
          )}

          <Input
            name="PRD"
            label="Prefix Directional"
            value={recordRequest.PRD ?? record.PRD ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={2}
          />
          <Input
            name="POD"
            label="Post Directional"
            value={recordRequest.POD ?? record.POD ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={2}
          />
          <HouseNumberInput
            recordRequest={recordRequest}
            setRecordRequest={setRecordRequest}
            lastMSAGRecord={lastMSAGRecord}
          />
          <Input
            name="HNS"
            label="House Number Suffix"
            value={recordRequest.HNS ?? record.HNS ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={4}
          />
          <Input
            name="LOC"
            label="Location"
            value={recordRequest.LOC ?? record.LOC ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={60}
          />

          {/* Additional Data Section */}
          {hasAddlDataPermission && (
            <ADDLDataSection
              addlDataRequest={addlDataRequest}
              setAddlDataRequest={setAddlDataRequest}
              record={record}
            />
          )}
        </div>

        {/* Right Column - Service Information */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Select
            label="Function of Change*"
            value={recordRequest.FOC ?? ""}
            name="FOC"
            onChange={(e) =>
              setRecordRequest((prev: ALIRecordRequest) => ({
                ...prev,
                FOC: e.target.value,
              }))
            }
            scale="s"
            labelSize="s"
            disabled={mode === "migrate" || mode === "create" ? true : false}
            message={
              mode !== "create"
                ? "Current Value: " +
                  (functionOfChangeOptions.find(
                    (option) => option.value === record.FOC,
                  )?.label || record.FOC)
                : undefined
            }
          >
            {mode === "edit" &&
              functionOfChangeEditOptions.map((option) => (
                <CalciteOption key={option.value} value={option.value}>
                  {option.label}
                </CalciteOption>
              ))}
            {mode === "create" &&
              functionOfChangeCreateOptions.map((option) => (
                <CalciteOption key={option.value} value={option.value}>
                  {option.label}
                </CalciteOption>
              ))}
            {mode === "migrate" &&
              functionOfChangeMigrateOptions.map((option) => (
                <CalciteOption key={option.value} value={option.value}>
                  {option.label}
                </CalciteOption>
              ))}
          </Select>
          {mode === "migrate" || mode === "create" ? (
            <NenaIDSelect
              recordRequest={recordRequest}
              setRecordRequest={
                setRecordRequest as React.Dispatch<
                  React.SetStateAction<ALIRecordRequest | MigrateForm>
                >
              }
            />
          ) : (
            <Input
              name="nenaID"
              label="NENA ID"
              value={recordRequest.CPF ?? record.CPF ?? ""}
              onChange={handleChange}
              scale="s"
              labelSize="s"
              readonly
            />
          )}
          <Select
            label="Class of Service*"
            value={recordRequest.CLS ?? ""}
            name="classOfService"
            onChange={(e) =>
              setRecordRequest((prev: ALIRecordRequest) => ({
                ...prev,
                CLS: e.target.value,
              }))
            }
            scale="s"
            labelSize="s"
          >
            {classOfServiceOptions.map((option) => (
              <CalciteOption key={option.value} value={option.value}>
                {option.label}
              </CalciteOption>
            ))}
          </Select>
          <Select
            label="Type of Service*"
            value={recordRequest.TYS ?? ""}
            name="typeOfService"
            onChange={(e) =>
              setRecordRequest((prev: ALIRecordRequest) => ({
                ...prev,
                TYS: e.target.value,
              }))
            }
            scale="s"
            labelSize="s"
          >
            {typeOfServiceOptions.map((option) => (
              <CalciteOption key={option.value} value={option.value}>
                {option.label}
              </CalciteOption>
            ))}
          </Select>
          <Input
            name="GeneralUse"
            label="General Use"
            value={recordRequest.GeneralUse ?? record.GeneralUse ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
          />
          <Input
            name="lecZipCode"
            label="Zip Code"
            value={recordRequest.lecZipCode ?? record.lecZipCode ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={5}
          />
          <Input
            name="lecZipExtension"
            label="Zip Extension"
            value={
              recordRequest.lecZipExtension ?? record.lecZipExtension ?? ""
            }
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={4}
          />
          <Input
            name="MainNPA"
            label="Area Code of Main Number"
            value={recordRequest.MainNPA ?? record.MainNPA ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={3}
          />
          <Input
            name="MainNumber"
            label="Main Number"
            value={recordRequest.MainNumber ?? record.MainNumber ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={7}
          />
          <Input
            name="Comments"
            label="Comments"
            value={recordRequest.Comments ?? record.Comments ?? ""}
            onChange={handleChange}
            scale="s"
            labelSize="s"
            max={30}
          />
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      {/* Additional Information Toggle Section */}
      <AdditionalInfoSection
        recordRequest={recordRequest}
        record={record}
        handleChange={handleChange}
        showAdditionalInfo={showAdditionalInfo}
        setShowAdditionalInfo={setShowAdditionalInfo}
      />
    </div>
  );
}
